// src/pages/Sotuv.js
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Sotuv.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { useSklad } from "../SkladContext";
import {
  getReceiptPrintExtras,
  openReceiptWindow as openSharedReceiptWindow,
} from "../receiptPrinting";

/* ================== Config ================== */
const PAGE_SIZE = 36;

/* ================== Decimal helpers ================== */
const MONEY_QUANT = 1000; // 0.001 aniqlik
// $ maydoniga so'm kiritib yuborilmasligi uchun ogohlantirish chegarasi
const USD_WARN_LIMIT = 1000;
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const toFixed3Str = (v) => {
  const n = Math.round(toNum(v) * MONEY_QUANT);
  const neg = n < 0;
  const abs = Math.abs(n);
  const ip = String(Math.floor(abs / MONEY_QUANT));
  const fp = String(abs % MONEY_QUANT).padStart(3, "0");
  return (neg ? "-" : "") + ip + "." + fp;
};
// >>> BU YERGA QO'YING <<<
const toSomInt = (v) => {
  const s = String(v ?? "").replace(/[^\d]/g, "");
  return s ? Math.max(0, parseInt(s, 10)) : 0;
};
const toSomRounded = (v) => Math.max(0, Math.round(toNum(v)));
/* ================== Sana helpers ================== */
const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/* ================== Formatters ================== */
const fmtUSD = (v, { min = 2, max = 4 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " so'm";
const fmtUZS = (v) => Math.round(toNum(v)).toLocaleString("uz-UZ") + " so'm";
const fmtDollar = (v, { min = 2, max = 4 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " $";


// --- STOCK helpers ---
const stockByProductId = (products, id) => {
  const p = products.find((pp) => pp.id === id);
  if (!p) return null;
  return stockOf(p);
};
const productById = (products, id) => products.find((pp) => pp.id === id);
//   helper
const clampQtyByStock = (qty, maxStk) => {
  // O'nlik miqdor (metr/kg): minimal 0.001, butun majburiy emas
  const q = Math.max(0.001, Number(qty) || 1);
  if (maxStk == null || maxStk <= 0) return q;  // stokni bilmasak – cheklamaymiz
  return Math.min(q, maxStk);
};

/* ================== Helpers ================== */
const stockOf = (p) => Number(p?.soni ?? p?.stock ?? 0);
const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const uniqById = (list) => {
  const seen = new Set(); const out = [];
  for (const it of list) { const k = String(it.id); if (!seen.has(k)) { seen.add(k); out.push(it); } }
  return out;
};

/* ================== Status utils ================== */
const normalizeBool = (v) =>
  v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");
const normalizeDate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : toLocalYMD(d);
};
const UI_STATES = { OVERDUE: "overdue_debtor", NOT_DUE: "debtor_not_due", NEUTRAL: "neutral", CREDITOR: "creditor" };
const SERVER2UI_STATUS = (s) => {
  switch ((s || "").toLowerCase()) {
    case "overdue_debtor": return UI_STATES.OVERDUE;
    case "debtor_not_due": return UI_STATES.NOT_DUE;
    case "neutral":        return UI_STATES.NEUTRAL;
    case "creditor":       return UI_STATES.CREDITOR;
    default:               return null;
  }
};
const computeUiStatus = ({ signedDebt, hasOverdue, dueDate }) => {
  const bal = toNum(signedDebt || 0);
  const due = normalizeDate(dueDate);
  if (bal < 0) {
    if (normalizeBool(hasOverdue)) return UI_STATES.OVERDUE;
    if (due && due < toLocalYMD(new Date())) return UI_STATES.OVERDUE;
    return UI_STATES.NOT_DUE;
  }
  if (bal > 0) return UI_STATES.CREDITOR;
  return UI_STATES.NEUTRAL;
};
const badgeClass = (ui) =>
  ui === UI_STATES.OVERDUE ? "badge-red" :
  ui === UI_STATES.NOT_DUE ? "badge-yellow" :
  ui === UI_STATES.CREDITOR ? "badge-green" : "";

/* ================== Narx helpers ================== */

// Backenddan kelgan narxlar roʻyxatini (label, price) ga normallashtirish
const priceMapForProduct = (p) => {
  if (!p) return [];

  // 1) Yangi backend: p.narxlar[]
  if (Array.isArray(p.narxlar) && p.narxlar.length) {
    return p.narxlar
      .map((n) => {
        const label = String(
          n.sarxturi ??
            n.narxturi ??
            n.narx_turi?.nom ??
            n.narx_turi?.name ??
            n.narx_turi_name ??
            n.sarx_turi ??
            n.label ??
            n.nom ??
            n.name ??
            n?.price_type?.name ??
            n?.type?.name ??
            ""
        ).trim();

        const price = toNum(
          n.narx ?? n.narxi ?? n.price ?? n.usd ?? n.price_usd ?? 0
        );

        return label ? { label, price } : null;
      })
      .filter(Boolean);
  }

  // 2) Eski backend: mahsulot obyektidagi toʻgʻridan-toʻgʻri maydonlar
  const legacy = [];
  const take = (lbl, ...cands) => {
    for (const k of cands) {
      if (p[k] != null && p[k] !== "") {
        legacy.push({ label: lbl, price: toNum(p[k]) });
        return;
      }
    }
  };
  take("donaga", "donaga", "donaga_narx", "narx_donaga", "narx_dollar", "narx");
  take("optom", "optom", "optom_narx", "narx_optom");
  take("vip", "vip", "vip_narx", "narx_vip");

  return legacy;
};

// Tanlangan label (yoki id) bo‘yicha USD narxni olish
const priceByTypeUSD = (p, labelOrId, priceTypes) => {
  const all = priceMapForProduct(p);
  if (!all.length) return null;

  // id -> nom
  let label = "";
  const byId = priceTypes.find((t) => String(t.id) === String(labelOrId));
  if (byId) label = byId.nom || byId.name || "";
  if (!label) label = String(labelOrId || "").trim();

  const lab = label.toLowerCase();
  const hit = all.find((x) => String(x.label || "").toLowerCase() === lab);
  return hit ? hit.price : null;
};


// Barcode mos kelishi: mahsulot barcodes (probel bilan) ichida kod bormi
const barcodeMatch = (barcodesStr, code) => {
  const c = String(code || "").trim();
  if (!c) return false;
  const tokens = String(barcodesStr || "").split(/\s+/).filter(Boolean);
  return tokens.includes(c);
};

// Skaner uchun: yorliqdagi QR mahsulot seriyasini (YT-3243) kodlaydi,
// zavod shtrix kodi esa barcodes ichida turadi - ikkalasi ham qabul qilinadi
const scanMatch = (p, code) => {
  const c = String(code || "").trim();
  if (!c) return false;
  if (barcodeMatch(p?.barcodes, c)) return true;
  return String(p?.seriyasi || "").trim().toLowerCase() === c.toLowerCase();
};

const computeDiscountValue = (mode, discount, subtotal) => {
  const sub = toNum(subtotal);
  if (mode === "percent") {
    const pct = Math.min(100, Math.max(toNum(discount), 0));
    return (pct * sub) / 100;
  }
  return Math.min(Math.max(toNum(discount), 0), sub);
};
const firstSkladIdFromCart = (cart, products) => {
  for (const c of cart) {
    if (c?.sklad) return c.sklad;                // avval cartdan
    const p = products.find((pp) => pp.id === c.id);
    if (p?.sklad) return p.sklad;                // keyin fallback
  }
  return null;
};
const buildReceiptPayload = ({ cart, seller, payType, discountValue, products, note, currency }) => {
  const items = cart.map((it) => ({
    product_name: it.name || "Noma’lum tovar",
    qty: Number(it.qty || 0),
    price: toFixed3Str(it.unitPrice),
    product_id: it.id || null,
    series: it.sku || null,
  }));
  return {
    buyer_name: seller?.name || "",
    buyer_phone: seller?.phone || "",
    buyer_location: "",
    pay_type: payType, // "naqd" | "nasiya" | "karta"
    seller: seller?.id || null,
    sklad: firstSkladIdFromCart(cart, products),
    discount: toFixed3Str(discountValue || 0),
    note: note || "",          // ✅ endi argumentdan keladi
    currency: currency || "som",  // narxlar shu valyutada (do'kon rejimi)
    items,
  };
};


/* ================== Mijoz rasmi ================== */
const customerImg = (s) =>
  s?.avatar ||
  s?.mijoz_rasmi_url ||
  (s?.mijoz_rasmi && s.mijoz_rasmi) ||
  (s?.mijozrasminomi && `/media/${s.mijozrasminomi}`) ||
  "https://i.pravatar.cc/80?img=1";

const mapSellerRecord = (s) => {
  const signedDebt = toNum(s?.qarhaq || s?.signedDebt || 0);
  const due = normalizeDate(s?.next_due_date || s?.dueDate || "");
  const hasOver = normalizeBool(s?.has_overdue);
  const srv = SERVER2UI_STATUS(s?.current_status);
  const uiState = srv || computeUiStatus({ signedDebt, hasOverdue: hasOver, dueDate: due });

  return {
    id: s?.id,
    name: s?.ismi || s?.name || "",
    phone: s?.telraqam1 || s?.telraqam2 || s?.phone || "",
    avatar: customerImg(s),
    debtAbs: Math.abs(signedDebt),
    signedDebt,
    uiState,
    tariff: s?.mijoztarifi || s?.tariff || "",
    current_status: s?.current_status || "",
    next_due_date: due,
    has_overdue: hasOver,
  };
};

/* ================== Product Card ================== */
const ProductCard = React.memo(function ProductCard({
  product, onAdd, usdPrice, somPrice, addPrice, currency, outOfStock, allowSell, selected
}) {
  const img = product.rasm_url || (product.rasm ? product.rasm : null);
  // Savatga do'kon valyutasidagi narx qo'shiladi (usd-do'kon: USD, som-do'kon: so'm)
  const handleAdd = () => { if (!outOfStock && allowSell) onAdd(product, addPrice ?? 0); };
  const priceLabel =
    product?.can_view_prices === false
      ? "Narx yopiq"
      : currency === "usd"
      ? (usdPrice != null ? fmtDollar(usdPrice) : "-")
      : (somPrice != null ? fmtUZS(somPrice) : "-");

  return (
    <div
      className={[
        "p-card",
        allowSell ? "clickable" : "disabled",
        outOfStock ? "oos" : "",
        selected ? "selected" : "",
      ].join(" ").trim()}
      onClick={handleAdd}
      title={outOfStock
        ? "Stok yo‘q"
        : !allowSell
        ? "Sizda bu sklad uchun savdo ruxsati yo'q"
        : "Savatchaga qo‘shish"}
    >
      {outOfStock && <span className="oos-tag">Stok yo‘q</span>}

      <div className="img-ph">
        {img ? (
          <img className="p-img" src={img} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="no-img" />
        )}
      </div>

      <div className="p-meta">
        <div className="p-name">{product.nom || product.name}</div>
        <div className="p-sku">{product.seriyasi || product.sku}</div>
        <div className="p-stock">{stockOf(product)} dona</div>
        <div className="p-price">{priceLabel}</div>
      </div>
    </div>
  );
});

/* ================== Cart Row ================== */
const CartRow = React.memo(function CartRow({
  item, onQtyAbs, onRemove, onUnitChange, onLineChange, curLabel = "so'm"
}) {
  const [unitStr, setUnitStr] = useState(String(item.unitPrice));
  const [lineStr, setLineStr] = useState(String(toNum(item.unitPrice) * item.qty));
  const [qtyStr, setQtyStr] = useState(String(item.qty));

  useEffect(() => {
    setUnitStr(String(item.unitPrice));
    setLineStr(String(toNum(item.unitPrice) * item.qty));
    setQtyStr(String(item.qty));
  }, [item.unitPrice, item.qty]);

  return (
    <div className="cart-row">
      <div className="cart-title">
        <div className="c-name">{item.name}</div>
        <div className="c-sku">{item.sku}</div>
      </div>

      <div className="cart-qty">
        <button onClick={() => onQtyAbs(item.id, Math.max(0.001, toNum(item.qty) - 1))} disabled={toNum(item.qty) <= 0.001}>–</button>
        <input
          className="in-num qty-in"
          type="text"
          inputMode="decimal"
          value={qtyStr}
          onChange={(e) => setQtyStr(e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))}
          onBlur={() => onQtyAbs(item.id, Math.max(0.001, toNum(qtyStr) || 1))}
        />
        <button onClick={() => onQtyAbs(item.id, toNum(item.qty) + 1)}>+</button>
      </div>

      <div className="cart-unit editable">
        <input
          className="in-num"
          type="text"
          inputMode="decimal"
          value={unitStr}
          onChange={(e) => setUnitStr(normDecStr(e.target.value))}
          onBlur={() => onUnitChange(item.id, unitStr)}
        />
        <small>{curLabel}</small>
      </div>

      <div className="cart-sum editable">
        <input
          className="in-num"
          type="text"
          inputMode="decimal"
          value={lineStr}
          onChange={(e) => setLineStr(normDecStr(e.target.value))}
          onBlur={() => onLineChange(item.id, lineStr)}
        />
        <small>{curLabel}</small>
      </div>

      <button className="cart-remove" onClick={() => onRemove(item.id)}>×</button>
    </div>
  );
});

/* ================== Receipt printing (80mm) ================== */
const renderReceiptHTML = (receipt, extras) => {
  const {
    payType = "naqd",
    dueDate = null,
    inUsd = 0,
    inUzsCash = 0,
    inUzsCard = 0,
    note = "",
  } = extras || {};

  // --- Raqam format: 3 xona, ortiqcha 0 va nuqtani olib tashlaydi ---
  const f3 = (n) => Number(n || 0).toFixed(3).replace(/\.?0+$/, "");

  /* --- CSS --- */
  const css = `
  @page { size: 80mm auto; margin: 4mm; }

  body {
    font-family: 'DejaVu Sans', Arial, sans-serif;
    font-size: 12px;
    margin: 0;
    padding: 0;
    color: #111;
    -webkit-print-color-adjust: exact;
  }

  /* ====== Bosh qism ====== */
  .brand-block {
    background: #000;
    text-align: center;
    padding: 10px 0;
    margin-bottom: 6px;
  }
  .brand-logo {
    width: 60px;
    height: auto;
    display: inline-block;
  }
  .h {
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    margin: 4px 0 8px;
    letter-spacing: 0.3px;
  }

  /* ====== Metadata ====== */
  .meta {
    font-size: 11px;
    text-align: left;
    margin-bottom: 4px;
    color: #333;
    line-height: 1.3;
  }

  /* ====== Ajratgich ====== */
  .s {
    border-top: 1px dashed #999;
    margin: 6px 0;
  }

  /* ====== Jadval ====== */
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  td {
    padding: 2px 0;
    vertical-align: top;
  }
  td.name {
    width: 68%;
    padding-right: 4px;
    white-space: normal;
    word-break: break-word;
  }
  td.calc {
    width: 32%;
    text-align: right;
    white-space: nowrap;
  }
  .code {
    color: #666;
    font-size: 10px;
    margin-left: 4px;
  }

  /* ====== Summalar ====== */
  .total b {
    font-size: 14px;
  }

  /* ====== Izoh bloki ====== */
  .note-block {
    font-size: 11px;
    color: #222;
    background: #f8f8f8;
    padding: 4px 6px;
    border-left: 3px solid #ccc;
    margin-top: 6px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* ====== Tugmalar (faqat ekranda) ====== */
  .btns {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    gap: 6px;
  }
  .btn {
    padding: 6px 10px;
    border: 1px solid #333;
    background: #fff;
    cursor: pointer;
    border-radius: 6px;
    font-size: 12px;
  }

  @media print {
    .btns { display: none; }
    body { background: #fff !important; }
  }
`;
    // --- Izoh (XSSdan tozalab) ---
  const noteStr = String(note || "").trim();
  const esc = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const noteHtml = noteStr
    ? `<div class="note-block"><b>Izoh:</b> ${esc(noteStr)}</div>`
    : "";


  // --- Items qatorlari: "NOM  KOD    qty × unit = line" ---
  const items = Array.isArray(receipt.items) ? receipt.items : [];
  const rows = items.map((it) => {
    const rawName = it.product_name || it.name || "Noma'lum";
    const name = rawName.length > 48 ? `<span style="font-size:11px">${rawName}</span>` : rawName;
    const code = it.series || it.sku || it.code || "";
    const qty  = Number(it.qty || it.miqdor || 0);
    const unit = Number(it.unit_price ?? it.price ?? 0); // birlik narx
    const line = qty * unit;

    return `
      <tr>
        <td class="name">${name}${code ? `<span class="code">${code}</span>` : ""}</td>
        <td class="calc">${f3(qty)} × ${f3(unit)} = ${f3(line)}</td>
      </tr>
    `;
  }).join("");

  // --- Sana/vaqt ---
  const dt = new Date(receipt.created_at || Date.now()).toLocaleString("uz-UZ", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });

  // --- Totallar ---
  const subtotal = f3(receipt.subtotal ?? 0);
  const discount = f3(receipt.discount ?? 0);
  const total    = f3(receipt.total ?? 0);
  const buyer    = receipt.buyer_name || "—";

  // --- To‘lov qismi (0 bo‘lsa ko‘rinmaydi) ---
  const lines = [];
  lines.push(`<tr><td class="name">To‘lov turi</td><td class="calc">${payType === "nasiya" ? "Nasiya" : "Naqd"}</td></tr>`);

  if (payType === "nasiya" && dueDate) {
    lines.push(`<tr><td class="name">Muddati</td><td class="calc">${dueDate}</td></tr>`);
  } else {
    if (Number(inUsd) > 0)
      lines.push(`<tr><td class="name">Terminal (so'm)</td><td class="calc">${Math.round(inUsd).toLocaleString("uz-UZ")} so'm</td></tr>`);
    if (Number(inUzsCash) > 0)
      lines.push(`<tr><td class="name">Naqd (so‘m)</td><td class="calc">${Math.round(inUzsCash).toLocaleString("uz-UZ")} so‘m</td></tr>`);
    if (Number(inUzsCard) > 0)
      lines.push(`<tr><td class="name">Kartadan (so‘m)</td><td class="calc">${Math.round(inUzsCard).toLocaleString("uz-UZ")} so‘m</td></tr>`);
  }

  // --- HTML ---
  return `
  <html><head><meta charset="utf-8"><title>Chek</title><style>${css}</style></head>
  <body>
    <div class="brand-block">
      <img src="/logo.jpg" alt="YUtech" class="brand-logo" />
    </div>

    <div class="h">SAVDO CHEK</div>
    <div class="meta">${dt}<br>Haridor: ${buyer}</div>
    <div class="s"></div>

    <table>${rows}</table>

    <div class="s"></div>
    <table>
      <tr><td class="name">Jami</td><td class="calc">${subtotal}</td></tr>
      <tr><td class="name">Chegirma</td><td class="calc">${discount}</td></tr>
      <tr class="total"><td class="name"><b>Yakuniy</b></td><td class="calc"><b>${total}</b></td></tr>
    </table>

    <div class="s"></div>
    <table>${lines.join("")}</table>

    ${noteHtml}

    <div class="btns">
      <button class="btn" onclick="window.print()">Print</button>
      <button class="btn" onclick="window.close()">Yopish</button>
    </div>
  </body></html>`;
};

// eslint-disable-next-line no-unused-vars
const openReceiptWindow = (receipt, extras) => {
  const html = renderReceiptHTML(receipt, extras);
  const win = window.open("", "_blank", "width=400,height=700");
  if (win) {
    win.document.write(html);
    win.document.close();
    return;
  }
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "-10000px";
  iframe.style.bottom = "-10000px";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
};

/* ================== Asosiy sahifa ================== */
export default function SalesPage() {
  const warehouseAccess = useMemo(() => getWarehouseAccess(), []);
  const [mobileTab, setMobileTab] = useState("products");

  /* Qidiruvlar */
  const [qProductsLeft, setQProductsLeft] = useState("");
  const [qSellers, setQSellers] = useState("");
  const [qCart, setQCart] = useState("");

  /* Filtrlar */
  const { activeSklad } = useSklad();
  const [warehouse, setWarehouse] = useState(activeSklad);
  const [priceType, setPriceType] = useState("");
  // Adminlar uchun global sklad o'zgarganda sync; cashierlar uchun init effektida o'rnatiladi
  useEffect(() => {
    if (warehouseAccess.allAccess) setWarehouse(activeSklad);
  }, [activeSklad, warehouseAccess.allAccess]);
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  // 🔥 YANGI: Qolgan / Qolmagan / Barchasi filtri
  // "available" – qolgan (stok > 0), "out" – tugagan (stok <= 0), "all" – hammasi
  const [stockFilter, setStockFilter] = useState("available");


  /* Mijoz/Reference */
  const [seller, setSeller] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  /* Mahsulotlar */
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const fetchingRef = useRef(false);
  const [renderLimit, setRenderLimit] = useState(PAGE_SIZE * 2);

  /* Savatcha */
  const [cart, setCart] = useState([]);

  /* To‘lov */
  const [payType, setPayType] = useState(""); // "naxt" | "nasiya"
  const [termDays, setTermDays] = useState("");


  /* Chegirma/izoh */
  const [discountMode, setDiscountMode] = useState("amount");
  const [discount, setDiscount] = useState("0");
  const [note, setNote] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  /* Valyuta ko'rinishi (filtr) + dollar kursi */
  const [currency, setCurrency] = useState("som"); // "som" | "usd"
  const [dollarRate, setDollarRate] = useState(0);

  /* Tanlangan do'kon (sklad) savdo valyutasi: usd bo'lsa dollarda savdo */
  const saleCurrency = useMemo(() => {
    const w = warehouses.find((x) => String(x.id) === String(warehouse));
    return w?.currency_mode === "usd" ? "usd" : "som";
  }, [warehouses, warehouse]);

  // Ko'rinish valyutasini do'kon valyutasiga moslash
  useEffect(() => {
    setCurrency(saleCurrency);
  }, [saleCurrency]);

  // Do'kon valyutasida formatlash va yorliq
  const curUnit = saleCurrency === "usd" ? "$" : "so'm";
  const fmtCur = useCallback(
    (v) => (saleCurrency === "usd" ? fmtDollar(v) : fmtUZS(v)),
    [saleCurrency]
  );
  // To'lov summasini o'qish: usd -> o'nlik, som -> butun
  const parseAmt = useCallback(
    (v) => (saleCurrency === "usd" ? String(toNum(v)) : String(toSomInt(v))),
    [saleCurrency]
  );


  /* Kassa kirim breakdown */
  const [inUsd, setInUsd] = useState("");
  const [inUzsCash, setInUzsCash] = useState("");
  const [inUzsCard, setInUzsCard] = useState("");
  const [changeMode, setChangeMode] = useState("cash");

  // 💳 To‘lov turi o‘zgarsa — boshqa maydonlarni tozalaymiz
  useEffect(() => {
    if (payType !== "naxt") {
      setInUsd("");
      setInUzsCash("");
      setInUzsCard("");
      setChangeMode("cash");
    }
  }, [payType]);

  /* ------- Init ------- */
  useEffect(() => {
    (async () => {
      try {
        const [w, t, c, b, p, k] = await Promise.all([
          api.get("sklad/"),
          api.get("narx-turi/"),
          api.get("maxsulot-kategoriyasi/"),
          api.get("brend/"),
          api.get(`maxsulot/?page=1&page_size=${PAGE_SIZE}&view=sale`),
          api.get("kurs/").catch(() => ({ data: {} })),
        ]);
        const W = asArr(w), T = asArr(t), C = asArr(c), B = asArr(b), P = asArr(p);
        setDollarRate(toNum(k?.data?.naqd) || 0);
        const visibleWarehouses = warehouseAccess.allAccess
          ? W
          : W.filter((item) => warehouseAccess.allowedIds.includes(Number(item.id)));
        setWarehouses([{ id: "all", nom: "Hammasi" }, ...visibleWarehouses]);

        // Cashier uchun: allowed sklad avtomatik tanlansin
        if (!warehouseAccess.allAccess) {
          if (warehouseAccess.allowedIds.length === 1) {
            setWarehouse(String(warehouseAccess.allowedIds[0]));
          } else {
            setWarehouse("all");
          }
        }
        setPriceTypes(T);
        setCategories([{ id: "all", nom: "Barchasi" }, ...C]);
        setBrands([{ id: "all", nom: "Barchasi" }, ...B]);
        setProducts(P);
        setHasMore(Boolean(p?.data?.next));

        // Default narx turi: "donaga" yoki birinchi
        const donaga = T.find((x) => (x.nom || "").toLowerCase() === "donaga");
        setPriceType(String(donaga ? donaga.id : T[0]?.id || ""));
      } catch (e) {
        console.log("Init load error", e?.response?.status, e?.response?.data);
        alert("Ma'lumotlarni yuklashda xatolik yuz berdi.");
      }
    })();
  }, [warehouseAccess.allAccess, warehouseAccess.allowedIds]);

  /* ------- Mijoz qidirish (debounce) ------- */
  const [sellerMatches, setSellerMatches] = useState([]);
  useEffect(() => {
    const q = qSellers.trim();
    if (q.length < 2) { setSellerMatches([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get(`sotuvchi/?search=${encodeURIComponent(q)}`);
        const arr = (res.data.results || res.data || []).map((s) => mapSellerRecord(s));
        setSellerMatches(arr);
      } catch (e) {
        console.log("Seller search error", e?.response?.status, e?.response?.data);
        setSellerMatches([]);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [qSellers]);


  /* ------- Filtrlar o‘zgarsa – 1-sahifadan ------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("page_size", String(PAGE_SIZE));
        params.set("view", "sale");
        if (warehouse !== "all") params.set("sklad", String(warehouse));
        if (category !== "all") params.set("kategoriya", String(category));
        if (brand !== "all") params.set("brand", String(brand));
        if (qProductsLeft.trim()) params.set("search", qProductsLeft.trim());
        const { data } = await api.get(`maxsulot/?${params.toString()}`);
        const rows = Array.isArray(data) ? data : data?.results || [];
        if (cancelled) return;
        setProducts(uniqById(rows));
        setPage(1);
        setHasMore(Boolean(data?.next));
      } catch (e) {
        console.log("PRODUCTS FIRST ERR", e?.response?.status, e?.response?.data);
        if (!cancelled) { setProducts([]); setHasMore(false); }
      } finally { if (!cancelled) setLoadingPage(false); }
    })();
    return () => { cancelled = true; };
  }, [warehouse, category, brand, qProductsLeft]);

  useEffect(() => {
    setRenderLimit(PAGE_SIZE * 2);
  }, [warehouse, category, brand, qProductsLeft, stockFilter]);

  /* ------- Keyingi sahifalarni olish ------- */
  const fetchMore = useCallback(async (nextPage) => {
    const params = new URLSearchParams();
    params.set("page", String(nextPage));
    params.set("page_size", String(PAGE_SIZE));
    params.set("view", "sale");
    if (warehouse !== "all") params.set("sklad", String(warehouse));
    if (category !== "all") params.set("kategoriya", String(category));
    if (brand !== "all") params.set("brand", String(brand));
    if (qProductsLeft.trim()) params.set("search", qProductsLeft.trim());
    const { data } = await api.get(`maxsulot/?${params.toString()}`);
    const rows = Array.isArray(data) ? data : data?.results || [];
    setProducts((prev) => uniqById([...prev, ...rows]));
    setHasMore(Boolean(data?.next));
  }, [warehouse, category, brand, qProductsLeft]);

  /* ------- Infinite scroll ------- */
  useEffect(() => {
    if (!loaderRef.current) return;
    const node = loaderRef.current;

    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting) return;
      setRenderLimit((v) => v + PAGE_SIZE);
      if (!hasMore || loadingPage) return;
      if (fetchingRef.current) return;

      fetchingRef.current = true;
      const next = page + 1;
      setPage(next);
      try {
        setLoadingPage(true);
        await fetchMore(next);
      } finally {
        setLoadingPage(false);
        fetchingRef.current = false;
      }
    }, { rootMargin: "400px 0px" });

    io.observe(node);
    return () => io.disconnect();
  }, [loaderRef, page, hasMore, fetchMore, loadingPage]);

  /* ------- Savatcha amallari ------- */
  const addToCart = useCallback((p, unitPrice) => {
  const stk = stockOf(p);
  if (stk <= 0) { alert(`${p.nom} (${p.seriyasi}) omborda yo‘q.`); return; }
  if (p?.can_sell === false) return;

  setCart((old) => {
    const ex = old.find((x) => x.id === p.id);
    const u = toNum(unitPrice);
    if (ex) {
      if (ex.qty + 1 > stk) {
        alert(`${p.nom} (${p.seriyasi}) yetarli emas. Omborda: ${stk} dona.`);
        return old;
      }
      const upd = old.map((x) =>
        x.id === p.id ? { ...x, qty: x.qty + 1, unitPrice: u } : x
      );
      const found = upd.find((x) => x.id === p.id);
      return [found, ...upd.filter((x) => x.id !== p.id)];
    }
    return [{
      id: p.id,
      name: p.nom,
      sku: p.seriyasi,
      unitPrice: u,
      qty: 1,
      sklad: p?.sklad ?? null,
    }, ...old];
  });
}, []);

  /* ------- 2D barcode skaner: kod bo'yicha mahsulotni topib savatga qo'shish ------- */
  const handleScan = useCallback(async (code) => {
    let prod = (products || []).find((p) => scanMatch(p, code));
    if (!prod) {
      try {
        const { data } = await api.get(`maxsulot/?barcode=${encodeURIComponent(code)}&view=sale`);
        const arr = asArr({ data });
        prod = arr && arr.length ? arr[0] : null;
      } catch (_) {}
    }
    if (!prod) {
      alert(`Kod topilmadi (shtrix/QR/seriya): ${code}`);
      return;
    }
    const label = resolvePriceLabel(seller, priceType, priceTypes);
    let usd = priceByTypeUSD(prod, label, priceTypes);
    if (usd == null && label !== "donaga") usd = priceByTypeUSD(prod, "donaga", priceTypes);
    const som = usd != null ? Math.round(toNum(usd) * toNum(dollarRate)) : 0;
    const addPrice = saleCurrency === "usd" ? (usd || 0) : som;
    addToCart(prod, addPrice);
  }, [products, seller, priceType, priceTypes, dollarRate, saleCurrency, addToCart]);

  // Global keydown: tez ketma-ket bosilгan tugmalar (skaner) + Enter
  useEffect(() => {
    let buf = "";
    let last = 0;
    const onKey = (e) => {
      const now = Date.now();
      if (now - last > 100) buf = "";  // sekin yozilса — buffer tozalanadi (odam yozyapti)
      last = now;
      if (e.key === "Enter") {
        const code = buf.trim();
        buf = "";
        if (code.length >= 4) {
          e.preventDefault();
          handleScan(code);
        }
        return;
      }
      if (e.key && e.key.length === 1) buf += e.key;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleScan]);

  const setQtyAbs = (id, newQty) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id);
      const clamped = clampQtyByStock(newQty, maxStk);
      if (maxStk != null && newQty > maxStk) {
        const p = productById(products, id);
        alert(
          `${p?.nom || "Mahsulot"} (${p?.seriyasi || ""}) uchun omborda ${maxStk} dona bor.`
        );
      }
      const upd = old.map((it) => (it.id === id ? { ...it, qty: clamped } : it));
      const found = upd.find((x) => x.id === id);
      return found ? [found, ...upd.filter((x) => x.id !== id)] : upd;
    });


  const removeItem = (id) => setCart((old) => old.filter((it) => it.id !== id));

  const changeUnit = (id, newUnit) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id);
      const upd = old.map((it) => {
        if (it.id !== id) return it;
        const qty = clampQtyByStock(it.qty, maxStk);
        return { ...it, unitPrice: Math.max(0, toNum(newUnit)), qty };
      });
      return upd;
    });


  const changeLineTotal = (id, newTotal) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id);
      const upd = old.map((it) => {
        if (it.id !== id) return it;
        const qty = clampQtyByStock(it.qty, maxStk);
        const unit = Math.max(0, toNum(newTotal) / Math.max(1, qty));
        return { ...it, unitPrice: unit, qty };
      });
      return upd;
    });



/* ------- UI helpers ------- */
const labelFromPriceTypeId = (id, list) =>
  (list.find(t => String(t.id) === String(id))?.nom || "").toLowerCase();

const resolvePriceLabel = (seller, selectedPriceTypeId, priceTypes) => {
  const has = (label) =>
    priceTypes.some(
      (pt) => String(pt.nom || pt.name || "").toLowerCase() === String(label).toLowerCase()
    );

  // mijoz tarifi ("vip", "optom", "donaga", ...)
  const sLab = String(seller?.tariff || seller?.mijoztarifi || "").toLowerCase();
  if (sLab && has(sLab)) return sLab;

  // selectdan tanlangan narx turi (id -> nom)
  const fromSelect = labelFromPriceTypeId(selectedPriceTypeId, priceTypes);
  if (fromSelect && has(fromSelect)) return fromSelect;

  // default
  return "donaga";
};

  const openCalc = () => {
    const exp = prompt("Hisob-kitob kiriting (masalan: 12.5*3 + 7/2)");
    if (exp == null) return;
    if (!/^[\d.+\-*/()%\s]+$/.test(exp)) { alert("Faqat raqam va + - * / ( ) % belgilariga ruxsat."); return; }
    try { /* eslint-disable no-new-func */
      const result = Function(`"use strict"; return (${exp})`)();
      alert(`Natija: ${result}`);
    } catch { alert("Ifoda xato."); }
  };

  // Dollar kursini shu yerdan o'zgartirish (faqat admin). Yangi savdolar shu kurs bilan.
  const changeRate = async () => {
    const cur = toNum(dollarRate);
    const input = prompt("Dollar kursi (1$ = necha so'm):", cur ? String(Math.round(cur)) : "");
    if (input == null) return;
    const val = toNum(input);
    if (val <= 0) { alert("Kurs 0 dan katta bo'lishi kerak."); return; }
    try {
      await api.patch("kurs/", { naqd: Math.round(val), karta: Math.round(val) });
      setDollarRate(val);
      alert(`Kurs yangilandi: 1$ = ${Math.round(val).toLocaleString("uz-UZ")} so'm`);
    } catch (e) {
      console.log("KURS PATCH ERR", e?.response?.status, e?.response?.data);
      alert("Kursni o'zgartirishda xatolik (faqat admin o'zgartira oladi).");
    }
  };

  const cartFiltered = useMemo(() => {
    const q = qCart.trim().toLowerCase();
    if (!q) return cart;
    return cart.filter((c) => `${c.name} ${c.sku}`.toLowerCase().includes(q));
  }, [cart, qCart]);

  const subtotal = cart.reduce((s, it) => s + toNum(it.unitPrice) * it.qty, 0);
  const disc = computeDiscountValue(discountMode, discount, subtotal);
  const totalDue = Math.max(0, subtotal - disc);

  /* ------- Paid / Remaining / Change (do'kon valyutasida, kurs bilan) -------
     To'lov maydonlari: Dollar ($), Naqd so'm, Karta so'm.
     som-do'kon: paid = dollar*kurs + naqd_som + karta_som
     usd-do'kon: paid = dollar + (naqd_som + karta_som)/kurs  */
  const _rate = Math.max(1, toNum(dollarRate));
  const paidShop =
    saleCurrency === "usd"
      ? toNum(inUsd) + (toNum(inUzsCash) + toNum(inUzsCard)) / _rate
      : toNum(inUsd) * _rate + toNum(inUzsCash) + toNum(inUzsCard);
  const remainingUsd = Math.max(0, totalDue - paidShop);
  const changeUsd = Math.max(0, paidShop - totalDue);
  // usd-do'konda o'nlik saqlanadi, som-do'konda butun
  const _round = (x) => (saleCurrency === "usd" ? Math.round(x * 100) / 100 : Math.round(x));
  const remainingUzs = _round(remainingUsd);
  const changeUzs = _round(changeUsd);

  // Qolgan to'lov / qaytimni ikkala valyutada ko'rsatish (so'm hisobi kurs bilan).
  // Asosiy summa do'kon valyutasida, qavsda ikkinchi valyuta taxminiy (≈).
  const fmtBoth = (amtInSaleCur) => {
    const a = toNum(amtInSaleCur);
    if (saleCurrency === "usd") {
      const som = Math.round(a * _rate);
      return `${fmtCur(a)} (≈ ${som.toLocaleString("uz-UZ")} so'm)`;
    }
    const usd = a / _rate;
    return `${fmtCur(a)} (≈ ${usd.toLocaleString("uz-UZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $)`;
  };

  /* ------- Tasdiqlash holati ------- */
  const EPS = 1e-9;
  const hasChange = payType === "naxt" && changeUsd > EPS;
  const canConfirm =
    !!seller &&
    cart.length > 0 &&
    (
      (payType === "naxt"   && (paidShop + EPS) >= totalDue) ||
      (payType === "nasiya" && toNum(termDays) > 0)
    );

  const buildChangeReason = ({ changeUsd: usd, note: saleNote }) => {
    const parts = [`Savdo qaytimi ${fmtCur(usd)}`];
    const cleanNote = String(saleNote || "").trim();
    if (cleanNote) parts.push(cleanNote);
    return parts.join(" | ").slice(0, 240);
  };
  const postChangeToSellerBalance = async ({ seller, changeUsd }) => {
    const sId = seller?.id;
    const amt = toNum(changeUsd);  // qaytim do'kon valyutasida
    if (!sId || amt <= 0) return { ok: false };

    // Do'kon valyutasi qaysi balansga yozilishini belgilaydi
    const deltaField = saleCurrency === "usd" ? "balance_delta_usd" : "balance_delta";

    try {
      // Balansni o'qib-yozmaymiz: faqat o'zgarish miqdorini yuboramiz.
      // Backend uni atomik qo'shadi, shuning uchun parallel savdo balansni buzmaydi.
      const res = await api.patch(`sotuvchi/${sId}/`, {
        [deltaField]: toFixed3Str(amt),
        balance_reason: buildChangeReason({
          changeUsd: amt,
          note,
        }),
        // izoh maydoni yo‘q, shuning uchun yubormasak ham bo‘ladi
      });
    
      return { ok: true, sellerData: res?.data || null };
    } catch (e) {
      console.log(
        "postChangeToSellerBalance ERROR",
        e?.response?.status,
        e?.response?.data || e.message
      );
      return { ok: false, error: e };
    }
  };

  /* Qaytim do'kon valyutasida beriladi, shuning uchun qoldiq ham,
     chiqim ham aynan o'sha valyutadagi kassadan tekshiriladi. */
  const ensureCashChangeAvailable = async (amount) => {
    if (amount <= 0) return { ok: true, availableAfterIncome: 0 };

    const res = await api.get("kassa/");
    const bucket = saleCurrency === "usd" ? res?.data?.usd : res?.data?.som;
    const currentCash = toNum(bucket?.cash ?? 0);
    // Shu savdoning o'z naqd kirimi ham qaytimga ishlatilishi mumkin
    const currentSaleCash = saleCurrency === "usd" ? toNum(inUsd) : toSomInt(inUzsCash);
    const availableAfterIncome = currentCash + currentSaleCash;

    return {
      ok: availableAfterIncome >= amount,
      currentCash,
      availableAfterIncome,
    };
  };

  const postCashChangeWithdraw = async ({ seller, changeUsd, changeUzs }) => {
    const amount = saleCurrency === "usd" ? toNum(changeUzs) : toSomRounded(changeUzs);
    if (amount <= 0) return { ok: true };

    try {
      await api.post("kassa/withdraw/", {
        nom: seller?.name || "Savdo qaytimi",
        reason: buildChangeReason({
          changeUsd,
          note,
        }),
        currency: saleCurrency,
        cash_som: amount,
        card_som: 0,
        terminal_som: 0,
      });
      return { ok: true };
    } catch (e) {
      console.log(
        "postCashChangeWithdraw ERROR",
        e?.response?.status,
        e?.response?.data || e.message
      );
      return { ok: false, error: e };
    }
  };


  /* --- Kassaga kirim: naqt rejimida /kassa/income/ ga yuborish --- */
  const postKassaIncome = async ({ buyerName, note }) => {
    const dollarCash = toNum(inUsd);      // dollar naqd -> USD kassa
    const somCash = toSomInt(inUzsCash);  // so'm naqd  -> som kassa
    const somCard = toSomInt(inUzsCard);  // so'm karta -> som kassa
    // Kirim aynan savdo do'koni kassasiga tushishi kerak, aks holda kassa
    // sahifasi do'kon bo'yicha filtrlaganda ko'rinmaydi (sklad=None bo'lib qoladi).
    const skladId = firstSkladIdFromCart(cart, products);

    // 1) Dollar naqd -> dollar kassa (naqd)
    if (dollarCash > 0) {
      await api.post("kassa/income/", {
        buyer_name: buyerName || "",
        cash_som: dollarCash,
        card_som: 0,
        terminal_som: 0,
        currency: "usd",
        sklad: skladId,
        note: (note || "").trim(),
      });
    }
    // 2) So'm naqd/karta -> som kassa
    if (somCash > 0 || somCard > 0) {
      await api.post("kassa/income/", {
        buyer_name: buyerName || "",
        cash_som: somCash,
        card_som: somCard,
        terminal_som: 0,
        currency: "som",
        sklad: skladId,
        note: (note || "").trim(),
      });
    }
  };
 

  /* ------- Tasdiqlash flow ------- */
  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      if (!seller) { alert("Avval haridorni tanlang."); return; }
    
      const bad = cart.find((c) => !c?.sklad);
      if (bad) {
        alert("Xato: savatchadagi ayrim mahsulotlarda sklad aniqlanmagan. Mahsulotni qayta tanlang.");
        return;
      }
    
      const isNasiya = payType === "nasiya";
      if (!(cart.length > 0 && (payType === "naxt" || (isNasiya && toNum(termDays) > 0)))) {
        alert("To‘lov turini tanlang va (agar nasiya bo‘lsa) muddat kiriting.");
        return;
      }

      // $ maydoniga so'm kiritib yuborilmasligi uchun tasdiq
      if (toNum(inUsd) >= USD_WARN_LIMIT) {
        const ok = window.confirm(
          `Siz dollar maydoniga ${toNum(inUsd).toLocaleString("uz-UZ")} $ kiritdingiz` +
          (_rate > 1 ? ` (≈ ${Math.round(toNum(inUsd) * _rate).toLocaleString("uz-UZ")} so'm).` : ".") +
          `\n\nHaqiqatan ham shuncha DOLLAR olindimi?\n` +
          `Agar bu so'm bo'lsa — "Bekor qilish" ni bosing va "So'm" maydoniga kiriting.`
        );
        if (!ok) return;
      }
    
      // yakuniy stok tekshiruvi
      const badLine = cart.find((c) => {
        const stk = stockByProductId(products, c.id);
        return stk != null && c.qty > stk;
      });
      if (badLine) {
        const p = productById(products, badLine.id);
        const stk = stockByProductId(products, badLine.id) ?? 0;
        alert(`${p?.nom || "Mahsulot"} (${p?.seriyasi || ""}) yetarli emas. Omborda: ${stk} dona. Miqdorni kamaytiring.`);
        return;
      }

      if (hasChange && changeMode === "cash") {
        const cashCheck = await ensureCashChangeAvailable(changeUzs);
        if (!cashCheck.ok) {
          alert(
            `Kassada qaytim uchun yetarli ${curUnit} yo'q.\nKerak: ${fmtCur(changeUzs)}\nMavjud (shu savdo naqd kirimi bilan): ${fmtCur(cashCheck.availableAfterIncome)}`
          );
          return;
        }
      }
    
      // === sizdagi hozirgi try ichidagi butun flow shu yerda davom etadi ===
      const tolovTuri = payType === "naxt" ? "naqd" : "nasiya";
    
      let nmuddat = null;
      if (tolovTuri === "nasiya") {
        const days = Math.max(1, Math.floor(toNum(termDays)));
        const d = new Date();
        d.setDate(d.getDate() + days);
        nmuddat = toLocalYMD(d);
      }
    
      const clientOrderId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    
      // Butun savat bitta so'rovda: backend hammasini bitta tranzaksiyada yozadi,
      // shuning uchun bir qator xato bersa yarim savdo qolib ketmaydi
      await api.post("sotilgan-maxsulot/", cart.map((c) => ({
        sotuvchi: seller.id,
        maxsulot: c.id,
        sklad: c?.sklad ?? null,
        sotilgansoni: c.qty,
        narx: toFixed3Str(c.unitPrice),
        tolov_turi: tolovTuri,
        nmuddat,
        izoh: note || "",
        client_order_id: clientOrderId,
        client_line_id: String(c.id),
      })));
    
      const receiptPayload = buildReceiptPayload({
        cart, seller, payType: tolovTuri, discountValue: disc, products, note,
        currency: saleCurrency,
      });
    
      let receipt = null;
      try {
        const res = await api.post("chek/", receiptPayload);
        receipt = res.data;
      } catch (e) {
        console.log("CHEK POST ERROR:", e?.response?.status, e?.response?.data);
        receipt = {
          ...receiptPayload,
          subtotal: toFixed3Str(subtotal),
          discount: toFixed3Str(disc),
          total: toFixed3Str(totalDue),
          created_at: new Date().toISOString(),
        };
      }
    
      if (payType === "naxt") {
        await postKassaIncome({ buyerName: seller?.name, note });
      }
    
      openSharedReceiptWindow(
        receipt,
        getReceiptPrintExtras(receipt, {
          payType: tolovTuri,
          dueDate: nmuddat,
          inTerminalSom: inUsd,
          inCashSom: inUzsCash,
          inCardSom: inUzsCard,
          note,
        })
      );
      const resultLines = ["Savdo, chek va kassa kirimi saqlandi."];

      if (hasChange) {
        if (changeMode === "balance") {
          const changeResult = await postChangeToSellerBalance({ seller, changeUsd });
          if (changeResult.ok) {
            if (changeResult.sellerData) {
              setSeller(mapSellerRecord({ ...seller, ...changeResult.sellerData }));
            }
            resultLines.push(`Qaytim balansga yozildi: ${fmtCur(changeUzs)}.`);
          } else {
            resultLines.push("Diqqat: qaytimni balansga yozib bo'lmadi. Balansni tekshiring.");
          }
        } else {
          const changeResult = await postCashChangeWithdraw({ seller, changeUsd, changeUzs });
          if (changeResult.ok) {
            resultLines.push(`Qaytim kassadan qaytarildi: ${fmtCur(changeUzs)}.`);
          } else {
            resultLines.push("Diqqat: qaytim kassadan yechilmadi. Kassa tarixini tekshiring.");
          }
        }
      }

      alert(resultLines.join("\n"));
      setCart([]); setPayType(""); setTermDays(""); setDiscount("0"); setNote("");
      setInUsd(""); setInUzsCash(""); setInUzsCard(""); setChangeMode("cash");
    
    } catch (e) {
      console.log("CONFIRM ERR", e?.response?.status, e?.response?.data);
      alert(`Saqlashda xato: ${e?.response?.status || ""} ${JSON.stringify(e?.response?.data || e.message)}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  // grid uchun filterlangan productlar
  const gridProducts = useMemo(() => {
    let list = products;

    if (stockFilter === "available") {
      // faqat qolgan maxsulotlar (stok > 0)
      list = list.filter((p) => stockOf(p) > 0);
    } else if (stockFilter === "out") {
      // faqat tugagan maxsulotlar (stok <= 0)
      list = list.filter((p) => stockOf(p) <= 0);
    }
    // "all" bo‘lsa hech narsa qilmaymiz

    return list;
  }, [products, stockFilter]);

  const visibleProducts = useMemo(
    () => gridProducts.slice(0, renderLimit),
    [gridProducts, renderLimit]
  );


  /* ======= UI ======= */
  return (
    <div className={`sale-wrap ${mobileTab === "checkout" ? "tab-checkout" : "tab-products"}`}>
      {/* ===== Title bar ===== */}
      <div className="title-line">
        <div className="title-head">
          <h2>Savdo amalga oshirish bo‘limi</h2>

          <button className="btn btn-ghost" onClick={openCalc}>Kalkulyator</button>
          <button
            className="btn btn-ghost"
            onClick={changeRate}
            title="Dollar kursini o'zgartirish"
          >
            Kurs: {dollarRate ? `1$ = ${Math.round(toNum(dollarRate)).toLocaleString("uz-UZ")}` : "—"}
          </button>
          <Link className="btn btn-ghost" to="/CheklarTarixi">Cheklar tarixi</Link>

          {seller && (
            <div className="mini-cust chip inline">
              <img src={customerImg(seller)} alt="" />
              <div className="mini-lines">
                <b className="mini-name">{seller.name}</b>
                <span className="mini-phone">{seller.phone || "—"}</span>
              </div>
              <span className={`debt-badge ${badgeClass(seller.uiState)}`}>
                {seller.uiState === UI_STATES.OVERDUE ? "Muddati o‘tgan: " :
                 seller.uiState === UI_STATES.NOT_DUE ? "Qarzdor: " :
                 seller.uiState === UI_STATES.CREDITOR ? "Haqdor: " : "Neytral: "}
                <b>{fmtUSD(seller.debtAbs)}</b>
              </span>
            </div>
          )}

          {/* o'ng chekkadagi seller qidiruv inputi */}
          <div className="title-spacer" />
          <div className="title-right">
            <div className="seller-search">
              <input
                className="input"
                placeholder="Haridorni qidiring"
                value={qSellers}
                onChange={(e) => setQSellers(e.target.value)}
              />
              {sellerMatches.length > 0 && (
                <div className="seller-dd">
                  {sellerMatches.map((s) => (
                    <div
                      key={s.id}
                      className="seller-dd-item"
                      onClick={() => { setSeller(s); setQSellers(""); }}
                    >
                      <img src={s.avatar || "https://i.pravatar.cc/80?img=1"} alt="" />
                      <div className="dd-text">
                        <b>{s.name}</b><span>{s.phone || "—"}</span>
                      </div>
                      <span className={`dd-badge ${
                        s.uiState === UI_STATES.CREDITOR ? "green" :
                        s.uiState === UI_STATES.NOT_DUE ? "yellow" :
                        s.uiState === UI_STATES.OVERDUE ? "red" : "gray"
                      }`}>
                        {fmtUSD(s.debtAbs)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==== Filtrlar + mahsulot qidirish ==== */}
      <div className="filters">
        <select value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.nom || w.name}</option>
          ))}
        </select>

        {/* narx turi */}
        <select value={priceType} onChange={(e) => setPriceType(e.target.value)}>
          {priceTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.nom || t.name}</option>
          ))}
        </select>

        {/* valyuta ko'rinishi (filtr): so'm / dollar */}
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          title="Narxlarni so'mda yoki dollarda ko'rsatish"
        >
          <option value="som">so'm</option>
          <option value="usd">$ dollar</option>
        </select>


        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.nom || c.name}</option>
          ))}
        </select>

        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{b.nom || b.name}</option>
          ))}
        </select>

        <input
          className="input search-in"
          placeholder="Mahsulot nomi, seriyasi bilan qidiring"
          value={qProductsLeft}
          onChange={(e) => setQProductsLeft(e.target.value)}
        />


        {/* 🔥 YANGI: Qolgan/qolmagan/barchasi filtri */}
        <select
          className="stock-filter"
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
        >
          <option value="available">Qolgan</option>
          <option value="out">Qolmagan</option>
          <option value="all">Barchasi</option>
        </select>
      </div>

      {/* GRID */}
      <div className="sale-grid">
        {/* LEFT */}
        <div className="left">
          <div className="left-box">
            <div className="p-scroll">
              <div className="p-grid">
                {visibleProducts.map((p) => {
                  const outOfStock = stockOf(p) <= 0;
                  const allowSell = p?.can_sell !== false;

                  // mijoz tarifiga yoki selectdagi narx turiga qarab label tanlaymiz
                  const label = resolvePriceLabel(seller, priceType, priceTypes);

                  // USD narxni topamiz
                  let usd = priceByTypeUSD(p, label, priceTypes);
                  if (usd == null && label !== "donaga") {
                    usd = priceByTypeUSD(p, "donaga", priceTypes);
                  }
                  // so'm = USD * joriy kurs (yaxlitlangan)
                  const som = usd != null ? Math.round(toNum(usd) * toNum(dollarRate)) : null;
                  // savatga qo'shiladigan narx: usd-do'kon -> USD, som-do'kon -> so'm
                  const addPrice = saleCurrency === "usd" ? usd : som;

                  const inCart = cart.some((c) => c.id === p.id);
                  return (
                    <ProductCard
                      key={p.id}
                      product={p}
                      usdPrice={usd}
                      somPrice={som}
                      addPrice={addPrice}
                      currency={currency}
                      outOfStock={outOfStock}
                      allowSell={allowSell}
                      selected={inCart}
                      onAdd={(prod, unitPrice) => addToCart(prod, unitPrice)}
                    />
                  );
                })}

              </div>
              {gridProducts.length === 0 && !loadingPage && <div className="empty">Mahsulot topilmadi</div>}
              {loadingPage && <div className="empty small">Yuklanmoqda…</div>}
              <div ref={loaderRef} style={{ height: 1 }} />
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="right">
          <div className="right-inner">
            <input
              className="input input-tight"
              placeholder="Tanlangan mahsulotlar ichidan qidiring"
              value={qCart}
              onChange={(e) => setQCart(e.target.value)}
            />

            {/* CART */}
            <div className="cart-list">
              {cartFiltered.map((it) => (
                <CartRow
                  key={it.id}
                  item={it}
                  onQtyAbs={setQtyAbs}
                  onRemove={removeItem}
                  onUnitChange={changeUnit}
                  onLineChange={changeLineTotal}
                  curLabel={saleCurrency === "usd" ? "$" : "so'm"}
                />
              ))}
              {cartFiltered.length === 0 && <div className="empty small">Ro‘yxat bo‘sh</div>}
            </div>

            {/* To‘lov turi + muddat */}
            <div className="pay-box">
              <div className="pay-seg">
                <button className={`seg-btn ${payType === "naxt" ? "on" : ""}`} onClick={() => setPayType("naxt")} type="button">Naqt</button>
                <button className={`seg-btn ${payType === "nasiya" ? "on" : ""}`} onClick={() => setPayType("nasiya")} type="button">Nasiya</button>

                <input
                  className="input input-kun inline"
                  type="number"
                  min="1"
                  placeholder="Muddat (kun)"
                  value={termDays}
                  onChange={(e) => setTermDays(e.target.value)}
                  disabled={payType !== "nasiya"}
                />
              </div>

              {/* Chegirma */}
              <div className="discount-wrap">
                <div className="discount-mode">
                  <label>
                    <input type="radio" name="dm" checked={discountMode === "amount"} onChange={() => setDiscountMode("amount")} /> Miqdor
                  </label>
                </div>
                <div className="discount-mode" style={{marginLeft:12}}>
                  <label>
                    <input type="radio" name="dm" checked={discountMode === "percent"} onChange={() => setDiscountMode("percent")} /> %
                  </label>
                </div>
                <div className="discount-input">
                  <label>Chegirma:</label>
                  <input
                    className="input input-kun"
                    type="text"
                    inputMode="decimal"
                    value={discount}
                    onChange={(e) => setDiscount(normDecStr(e.target.value))}
                    onBlur={(e) => setDiscount(normDecStr(e.target.value))}
                  />
                  <span className="muted">{discountMode === "amount" ? curUnit : "%"}</span>
                </div>
              </div>

              {/* Totallar */}
              <div className="totals-inline">
                <div>Jami: <b>{fmtCur(subtotal)}</b></div>
                <div>Chegirma: <b>{fmtCur(disc)}</b></div>
                <div className="grand">Yakuniy: <b>{fmtCur(totalDue)}</b></div>
              </div>

              {/* Naqt rejimida – qolgan/qaytim indikatorlari */}
              {payType === "naxt" && totalDue > 0 && (
                <div className="pay-hints">
                  {remainingUsd > 0 && (
                    <div className="hint need">
                      Qolgan to‘lov: <b>{fmtBoth(remainingUzs)}</b>
                    </div>
                  )}
                  {hasChange && (
                    <div className="hint change">
                      <div>Qaytim: <b>{fmtBoth(changeUzs)}</b></div>
                      <div className="change-settle">
                        <span className="change-settle-label">Qaytimni:</span>
                        <div className="mini-seg">
                          <button
                            type="button"
                            className={changeMode === "cash" ? "on" : ""}
                            onClick={() => setChangeMode("cash")}
                          >
                            Kassadan qaytarish
                          </button>
                          <button
                            type="button"
                            className={changeMode === "balance" ? "on" : ""}
                            onClick={() => setChangeMode("balance")}
                          >
                            Balansga yozish
                          </button>
                        </div>
                      </div>
                      <div className="change-settle-note">
                        {changeMode === "cash"
                          ? `Kassadan ${fmtCur(changeUzs)} chiqim qilinadi.`
                          : `Mijoz balansiga ${fmtCur(changeUzs)} yoziladi.`}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* KASSA breakdown — faqat NAQT rejimida.
                  Har doim: Dollar (naqd $), Naqd so'm, Karta so'm.
                  Kurs bilan do'kon valyutasiga hisoblanadi. */}
              {payType === "naxt" && (
                <div className="cash-in">
                  <div className="line">
                    <label>$</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={inUsd}
                      onChange={(e) => setInUsd(String(toNum(e.target.value)))}
                    />
                  </div>
                  {toNum(inUsd) >= USD_WARN_LIMIT && (
                    <div className="hint need" style={{ color: "#c0392b", fontWeight: 600 }}>
                      ⚠️ Bu <b>dollar</b> maydoni: {toNum(inUsd).toLocaleString("uz-UZ")} $
                      {_rate > 1 ? ` (≈ ${Math.round(toNum(inUsd) * _rate).toLocaleString("uz-UZ")} so'm)` : ""}.
                      So'm bo'lsa, pastdagi "So'm" maydoniga kiriting.
                    </div>
                  )}
                  <div className="line">
                    <label>So'm</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={inUzsCash}
                      onChange={(e) => setInUzsCash(String(toSomInt(e.target.value)))}
                    />
                  </div>
                  <div className="line">
                    <label>So'm (Karta)</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={inUzsCard}
                      onChange={(e) => setInUzsCard(String(toSomInt(e.target.value)))}
                    />
                  </div>
                </div>
              )}



              <div className="note-box">
                <label>Izoh:</label>
                <textarea
                  className="input note-textarea"
                  placeholder={payType === "nasiya" ? "Nasiya shartlari, kelishuv, eslatma..." : "Ixtiyoriy izoh..."}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div className="actions">
                <button className="btn btn-ghost" onClick={() => setCart([])}>Tozalash</button>
                <button
                  className="btn btn-primary"
                  disabled={!canConfirm || isSubmitting}
                  onClick={handleConfirm}
                >
                  {isSubmitting ? "Yuborilmoqda..." : "Tasdiqlash"}
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom switcher */}
      <div className="mobile-bar">
        <button
          type="button"
          className={`mb-btn ${mobileTab === "products" ? "on" : ""}`}
          onClick={() => setMobileTab("products")}
        >
          Mahsulotlar
        </button>
        <button
          type="button"
          className={`mb-btn ${mobileTab === "checkout" ? "on" : ""}`}
          onClick={() => setMobileTab("checkout")}
        >
          Haridor &amp; Savdo
        </button>
      </div>
    </div>
  );
}
